<template>
  <div class="settings-block">
    <div class="settings-block__title">
      <div class="settings-block__title-text">
        {{t('Traffic Sources')}}
      </div>
      <div class="settings-block__title-buttons">
        <a href="https://help.bemob.com/docs/advanced-integrations-introduction" class="read-guide">
          <ui-tooltip-icon
            preset="help"
            :text="t('Learn more about the integration processes with BeMob')"
          />
          <span class="read-guide ml-6">
            {{t('Read Guide')}}
          </span>
        </a>
        <ui-button
          :text="t('Add Integration')"
          icon="fas fa-plus"
          button-style="accent"
          class="ml-8"
          @click="chooseNewIntegration"
        />
      </div>
    </div>
    <div>
      <ui-table
        :loading="!integrationsUploaded"
        :headers="headers"
        :items="tableRows"
        :use-search="false"
        :virtualize="false"
        :rows-per-page-count="rowsOnPage"
        class="table-integration main-table"
      >
        <template #items="{ item }">
          <div
            class="table-cell"
            :class="{ 'plan-limits-warning__td': hasPlanLimitsRestriction(item.planLimits) }"
          >
            {{ item.name }}
          </div>
          <div
            class="table-cell"
            :class="{ 'plan-limits-warning__td': hasPlanLimitsRestriction(item.planLimits) }"
          >
            {{ integrationNamesByType[item.type]?.name || item.type }}
          </div>
          <div
            class="table-cell"
            :class="{ 'plan-limits-warning__td': hasPlanLimitsRestriction(item.planLimits) }"
          >
            <span
              :class="{
                status: true,
                statusActive: item.status === 'active',
                statusNotActive:
                  item.status !== 'active' || hasPlanLimitsRestriction(item.planLimits),
              }"
            >
              {{ getStatus(item) }}
            </span>
            <ui-icon
              v-if="!hasPlanLimitsRestriction(item.planLimits)"
              @click="updateStatus(item)"
              size="10"
              class="update-status"
              :class="{ statusRotate: checkingStatusId === item.id }"
              icon="fas fa-sync-alt"
            />
          </div>
          <div
            class="table-cell"
            :class="{ 'plan-limits-warning__td': hasPlanLimitsRestriction(item.planLimits) }"
          >
            <div class="main-table__action-buttons">
              <button
                class="main-table__action-button"
                :class="{
                  'main-table__action-button--disabled': hasPlanLimitsRestriction(item.planLimits),
                }"
                type="button"
                @click="editIntegration(item)"
                :disabled="hasPlanLimitsRestriction(item.planLimits)"
              >
                <ui-icon
                  :color="
                    hasPlanLimitsRestriction(item.planLimits) ? $colors.gray40 : $colors.green
                  "
                  class="mr-4"
                  icon="fas fa-pen"
                />
                {{ t('Edit') }}
              </button>

              <button
                class="main-table__action-button"
                type="button"
                @click="deleteIntegration(item.id)"
                :style="{ color: 'var(--color-red)' }"
              >
                <ui-icon :color="$colors.red" class="mr-4" icon="fas fa-trash-alt" />
                {{ t('Delete') }}
              </button>

              <ui-tooltip-icon
                v-if="hasPlanLimitsRestriction(item.planLimits)"
                class="plan-limits-warning__tooltip--integrations"
                preset="error"
                :text="
                  t(
                    'This option is no longer active due to the subscription change to the plan which doesn’t include this option or this add-on is switched off in your subscriptions settings.',
                  )
                "
              />
            </div>
          </div>
        </template>
      </ui-table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import {
  deleteIntegrationApiRequest,
  getIntegrationCheckStatusApiRequest,
  type IntegrationListItem,
} from '@/services/api/integrations-api';
import { populateIntegrationsStoreCacheApiRequest } from '@/services/api/api-services/integrations';
import { useColors, useI18n, usePlanLimits } from '@/composables';
import { useStore } from '@/store';
import { $emitter } from '@/services/emitter';

type IntegrationItem = IntegrationListItem & {
  planLimits?: string;
};

const store = useStore();
const { t } = useI18n();
const $colors = useColors();
const { hasPlanLimitsRestriction, integrationsPlanLimits } = usePlanLimits(undefined, false);

const integrations = computed(() => store.cache.integrations.data as IntegrationItem[]);
const integrationsUploaded = computed(() => store.cache.integrations.isUploaded);
const checkingStatusId = ref('');
const rowsOnPage = 10;

const integrationNamesByType = computed(() => ({
  propeller: { name: 'PropellerAds' },
  exoclick: { name: 'ExoClick' },
  mgid: { name: 'MGID' },
  revcontent: { name: 'Revcontent' },
  'facebook-conversions': { name: 'Facebook' },
  trafficjunky: { name: 'TrafficJunky' },
}));

const headers = computed(() => [
  {
    title: t('Integration Name'),
    align: 'left',
    value: 'name',
    sortable: true,
  },
  {
    title: t('Network'),
    align: 'left',
    value: 'type',
    sortable: true,
  },
  {
    title: t('Status'),
    align: 'left',
    value: 'status',
    sortable: true,
  },
  {
    title: '',
    align: 'left',
    value: 'action',
    sortable: false,
  },
]);

const tableRows = computed(() =>
  integrations.value.map(item => ({
    ...item,
    planLimits: integrationsPlanLimits(item.type),
  })),
);

const createIntegrationOpen = (data: Record<string, unknown>) => {
  if (data.type === 'facebook-conversions') {
    $emitter.emit('open-dialog', { name: 'CreateIntegrationDialogFacebook', data });
    return;
  }
  $emitter.emit('open-dialog', { name: 'CreateIntegrationDialog', data });
};

const chooseNewIntegration = () => {
  $emitter.emit('open-dialog', {
    name: 'ChooseIntegrationDialog',
    data: { createIntegrationOpen },
  });
};

const updateStatus = (item: IntegrationItem) => {
  const { id } = item;
  checkingStatusId.value = id;

  getIntegrationCheckStatusApiRequest(id)
    .then((data: IntegrationItem) => {
      store.cache.updateIntegrationsCache(data);
    })
    .catch((error: { response?: { data?: { payload?: IntegrationItem } } }) => {
      const payload = error?.response?.data?.payload;
      if (!payload) return;
      store.cache.updateIntegrationsCache(payload);
    })
    .finally(() => {
      checkingStatusId.value = '';
    });
};

const deleteIntegration = (id: string) => {
  $emitter.emit('open-dialog', {
    name: 'ConfirmDialog',
    data: {
      message: t(
        'Removing the integrated element will end automatic cost passing for all the associated campaigns. Previously collected cost data will not be deleted.',
      ),
      onConfirm: () => {
        deleteIntegrationApiRequest(id).finally(() => {
          const updated = store.cache.integrations.data.filter(item => item.id !== id);
          store.cache.populateIntegrationsCache(updated);
        });
      },
    },
  });
};

const editIntegration = (data: Record<string, unknown>) => {
  if (data.type === 'facebook-conversions') {
    $emitter.emit('open-dialog', { name: 'CreateIntegrationDialogFacebook', data });
    return;
  }
  $emitter.emit('open-dialog', { name: 'CreateIntegrationDialog', data });
};

const getStatus = (integration: IntegrationItem) => {
  if (hasPlanLimitsRestriction(integration.planLimits)) {
    return t('Access Limited');
  }
  if (integration.status === 'active') {
    return t('active');
  }
  return integration.statusMessage;
};

const loadIntegrations = async () => {
  store.setLoading(true);
  try {
    await populateIntegrationsStoreCacheApiRequest();
  } finally {
    store.setLoading(false);
  }
};

onMounted(async () => {
  await loadIntegrations();
  $emitter.on('integrations-changed', loadIntegrations);
});

onUnmounted(() => {
  $emitter.off('integrations-changed', loadIntegrations);
});
</script>

<style lang="scss" scoped>

  .section-title{
   text-transform: capitalize;
  }
  .read-guide{
    text-decoration: none;
    color: var(--color-black);
    display: flex;
    white-space: nowrap;
    align-items: center;
    .v-tooltip{
      line-height: 0;
    }
  }

  :deep(.table-integration) {
    .ui-table__inner {
      table-layout: fixed;
      min-width: 450px;
    }

    .table-header > .table-cell,
    .table-body .table-row > .table-cell {
      text-align: left !important;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      padding-right: 5px !important;

      &:nth-child(1),
      &:nth-child(2) {
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      }

      &:nth-child(4) {
        width: 140px !important;
        padding-right: 5px !important;

        &:lang(ru) {
          width: 185px !important;
        }
        &:lang(es) {
          width: 165px !important;
        }
      }
    }

    .table-body .table-row > .table-cell:nth-child(3) {
      line-height: 18px;

      i {
        line-height: 16px;
      }
    }

    .table-body .table-row > .table-cell.plan-limits-warning__td {
      border-top: 1px solid var(--color-red) !important;
      border-bottom: 1px solid var(--color-red) !important;
    }
  }

  .main-table__action-buttons{
    display: flex;
    justify-content: flex-end;
  }

  .main-table__action-button{
    display: flex;
    align-items: center;
    color:var(--color-green);
    margin-left:16px;
    white-space: nowrap;
  }
  
  .main-table__action-button--disabled{
    color:var(--color-gray40);
  }

  .statusActive{
    color:var(--color-green);
  }

  .statusNotActive{
    color:var(--color-red);
  }

  .statusRotate{
    will-change: transform;
    animation: rotation 2s infinite linear;
  }

  @keyframes rotation {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(359deg);
    }
  }

  .update-status {
    opacity: 0.9;
    margin-left: 4px;
    font-weight: normal;
    cursor: pointer;
  }

  .main-table__action-buttons :deep(.plan-limits-warning__tooltip--integrations) {
    margin-left: 6px;
  }
</style>
